import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { FormattedMessage, useIntl } from 'react-intl';
import { DISPLAY_UNIT_ENUM } from '~/modules/resourcing/common/enums';
import { useMeContext } from '~/modules/me';
import { getReactNumberFormatFromMe } from '~/modules/common/numbers';
import AllocationInput from './AllocationInput';
import { ALLOCATION_PERIOD } from './AllocationPeriodTabs';

export const MAX_PERCENTAGE = 1000;

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2)
  },
  rowLabel: {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
    width: theme.spacing(16),
    flexShrink: 0,
    paddingTop: theme.spacing(0.75)
  },
  toggleGroup: {
    margin: theme.spacing(0)
  },
  toggleButton: {
    ...theme.typography.body2,
    height: theme.spacing(4),
    minWidth: theme.spacing(12),
    padding: theme.spacing(0.5, 2),
    textTransform: 'none',
    color: theme.palette.text.secondary,
    '&.Mui-selected': {
      color: theme.palette.text.primary
    }
  }
}));

const getMaxHoursForEntirePeriod = ({ startDate, endDate }) => {
  const totalDays = endDate.diff(startDate, 'days').toObject().days + 1;

  return totalDays * 24;
};

const EntirePeriod = ({
  dateRange,
  selectedAllocationPeriod,
  hoursValue,
  onHoursValueChange,
  percentageValue,
  onPercentageValueChange,
  error,
  disabled,
  displayUnit,
  onDisplayUnitChange
}) => {
  const classes = useStyles();
  const me = useMeContext();
  const { decimalScale } = getReactNumberFormatFromMe(me);
  const { formatMessage } = useIntl();
  const maxHoursForEntirePeriod = getMaxHoursForEntirePeriod({ ...dateRange });
  const setByLabel = formatMessage({ id: 'allocationByPeriod.setBy' });

  const handleToggleChange = useCallback(
    (_, newValue) => {
      if (newValue !== null) {
        onDisplayUnitChange(newValue);
      }
    },
    [onDisplayUnitChange]
  );

  return (
    <>
      <div className={classes.row}>
        <Typography className={classes.rowLabel}>{setByLabel}</Typography>
        <ToggleButtonGroup
          exclusive
          value={displayUnit}
          onChange={handleToggleChange}
          className={classes.toggleGroup}
          aria-label={setByLabel}
        >
          <ToggleButton
            value={DISPLAY_UNIT_ENUM.HOURS}
            className={classes.toggleButton}
          >
            <FormattedMessage id="allocationByPeriod.unitToggle.hours" />
          </ToggleButton>
          <ToggleButton
            value={DISPLAY_UNIT_ENUM.PERCENTAGE}
            className={classes.toggleButton}
          >
            <FormattedMessage id="allocationByPeriod.unitToggle.percent" />
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      <div className={classes.row}>
        <Typography className={classes.rowLabel}>
          <FormattedMessage id="allocationByPeriod.allocate" />
        </Typography>
        {displayUnit === DISPLAY_UNIT_ENUM.PERCENTAGE ? (
          <AllocationInput
            value={parseFloat(percentageValue.toFixed(decimalScale))}
            selectedAllocationPeriod={selectedAllocationPeriod}
            displayUnit={DISPLAY_UNIT_ENUM.PERCENTAGE}
            maxValue={MAX_PERCENTAGE}
            onChange={onPercentageValueChange}
            error={Boolean(error)}
            helperText={error}
            disabled={disabled}
          />
        ) : (
          <AllocationInput
            value={parseFloat(hoursValue.toFixed(decimalScale))}
            selectedAllocationPeriod={selectedAllocationPeriod}
            displayUnit={DISPLAY_UNIT_ENUM.HOURS}
            maxValue={maxHoursForEntirePeriod}
            onChange={onHoursValueChange}
            error={Boolean(error)}
            helperText={error}
            disabled={disabled}
          />
        )}
      </div>
    </>
  );
};

EntirePeriod.propTypes = {
  dateRange: PropTypes.shape({
    startDate: PropTypes.object.isRequired,
    endDate: PropTypes.object.isRequired
  }).isRequired,
  selectedAllocationPeriod: PropTypes.oneOf(Object.values(ALLOCATION_PERIOD))
    .isRequired,
  hoursValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  percentageValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  onHoursValueChange: PropTypes.func.isRequired,
  onPercentageValueChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  displayUnit: PropTypes.string,
  onDisplayUnitChange: PropTypes.func
};

export default EntirePeriod;
